import moment from 'moment'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import CurrencyFormatter from '@/components/currency-formatter'
import ResolvePaymentButton from '@/features/Payments/resolve-payment-button'
import useRole from '@/hooks/use-role'
import { colors, fontWeights } from '@/styles'
import StatusBadge from '@/components/status-badge'
import { Box } from '@/components/structure'
import { Label, ParagraphRegular } from '@/components/text'

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 600px;
  border-radius: 12px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  margin: 0 auto;
  padding: 24px;
  background-color: ${colors.white};
  cursor: pointer;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DueText = styled(ParagraphRegular)`
  color: ${props => (props.showRedText ? colors.red64 : colors.grey56)};
  strong {
    font-weight: ${fontWeights.medium};
  }
`

const PaymentCard = ({ data = {}, onClick, isPadActive, isPadRequested }) => {
  const { t } = useTranslation()
  const isTenant = useRole() === 'tenant'
  const todayDate = moment().format('YYYY-MM-DD')

  const paidOnDate = moment(data.paid_at).format('MMM DD, YYYY')
  const isPad = data?.request_type === 'pad'
  const isPending = data.state_machine === 'pending'

  const wasDueOnDate = `${t('l.listing.view.payment_was_due_on.text', 'was due on')} ${moment(
    data.due_date
  ).format('MMM DD, YYYY')}`

  const isDueInDate = `${t('l.listing.view.payment_is_due_in.text', 'is due in')} ${moment(
    data.due_date
  ).diff(todayDate, 'days')} ${t('l.listing.view.payment_days.text', 'days')}`

  const formatDate = moment(data.due_date).calendar(null, {
    lastWeek: function (now) {
      return `[${wasDueOnDate}]`
    },
    lastDay: `[${t('l.listing.view.payment_was_due_yesterday.text', 'was due Yesterday')}]`,
    sameDay: `[${t('l.listing.view.payment_is_due_today.text', 'is due Today')}]`,
    nextDay: `[${t('l.listing.view.payment_is_due_tomorrow.text', 'is due Tomorrow')}]`,
    nextWeek: function (now) {
      return `[${isDueInDate}]`
    },
    sameElse: function (now) {
      if (this.isBefore(todayDate)) {
        return `[${wasDueOnDate}]`
      }
      return `[${isDueInDate}]`
    }
  })

  const renderPadPayment = () => {
    switch (data.payment_status) {
      case 'collecting_funds':
      case 'remitting_funds':
      case 'upcoming':
        return (
          <DueText>
            <Trans i18nKey="l.listing.view.pad_upcoming_payment.text">
              Payment of{' '}
              <strong>
                <CurrencyFormatter value={data.charged} showCents />
              </strong>
            </Trans>{' '}
            will be automatically charged on {moment(data.due_date).format('MMM DD, YYYY')}
          </DueText>
        )
      default:
        return (
          <DueText>
            <Trans
              i18nKey="l.listing.view.pad_paid_on.text"
              values={{ date: paidOnDate }}
              defaults="Paid via PAD on {{date}}"
            />
          </DueText>
        )
    }
  }

  const renderPaymentText = () => {
    const isPastDate = moment(data.due_date).isBefore(todayDate)

    const getPendingText = () => {
      if (!isTenant)
        return (
          <DueText showRedText={isPastDate}>
            <Trans i18nKey="l.listing.view.payment_of.text">
              Payment of{' '}
              <strong>
                <CurrencyFormatter value={data.charged} showCents />
              </strong>
            </Trans>{' '}
            {formatDate}
          </DueText>
        )

      switch (data.payment_status) {
        case 'collecting_funds':
          return (
            <Box top={8}>
              <StatusBadge
                title={t('b.payment.view.payment_pending.text', 'Payment pending')}
                type="warning"
                size="small"
              />
            </Box>
          )
        case 'incomplete':
          return (
            <Box top={8}>
              <StatusBadge
                title={t('b.payment.view.payment_incomplete.text', 'Payment incomplete')}
                type="warning"
                size="small"
              />
            </Box>
          )
        default:
          return (
            <DueText showRedText={isPastDate}>
              <Trans i18nKey="l.listing.view.payment_of.text">
                Payment of{' '}
                <strong>
                  <CurrencyFormatter value={data.requested_amount} showCents />
                </strong>
              </Trans>{' '}
              {formatDate}
            </DueText>
          )
      }
    }

    return isPending ? (
      getPendingText()
    ) : (
      <DueText>
        <Trans
          i18nKey="l.listing.view.paid_on.text"
          values={{ date: paidOnDate }}
          defaults="Paid on {{date}}"
        />
      </DueText>
    )
  }

  const renderText = isPad ? renderPadPayment() : renderPaymentText()

  const renderPadStatus = () => {
    const errorMessage = data.error_message
    const isTenantError = data.error_side === 'tenant'

    const showTenantError = isTenant && isTenantError
    const showLandlordError = !isTenant && !isTenantError

    const failedTooltip =
      showTenantError || showLandlordError ? (
        <Trans
          t={t}
          key="b.pad_payments.landlord_tooltip_error.text"
          i18nKey="b.pad_payments.landlord_tooltip_error.text"
          values={{ errorMessage }}
        >
          We have detected that a payment failed to process because {{ errorMessage }}. Please
          contact Liv Support at support@liv.rent to get this resolved.
        </Trans>
      ) : undefined

    switch (data.payment_status) {
      case 'upcoming':
        if (isPadRequested && !isPadActive) {
          return (
            <Box top={16}>
              <StatusBadge
                title={t(
                  'b.payment.view.payment_pad_before_confirm.text',
                  'Pending Tenant Confirmation'
                )}
                type="warning"
                size="small"
              />
            </Box>
          )
        } else {
          return (
            <Box top={16}>
              <StatusBadge
                title={t('b.payment.view.recurring_payments.text', 'Recurring Payments')}
                type="blue"
                size="small"
              />
            </Box>
          )
        }
      case 'fail':
        return (
          <Box top={16}>
            <StatusBadge
              title={t('b.payment.view.payments_failed.text', 'Payments Failed')}
              type="error"
              size="small"
              tooltip={failedTooltip}
              tooltipTheme="secondary"
            />
          </Box>
        )
      case 'collecting_funds':
        return (
          <Box top={16}>
            <StatusBadge
              title={t('b.payment.view.collecting_funds.text', 'Collecting funds')}
              type="info"
              size="small"
            />
          </Box>
        )
      case 'remitting_funds':
        return (
          <Box top={16}>
            <StatusBadge
              title={t('b.payment.view.remitting_funds.text', 'Remitting funds')}
              type="warning"
              size="small"
            />
          </Box>
        )
      default:
        return null
    }
  }

  return (
    <Wrapper onClick={onClick} data-testid={`payment-${data.id}`}>
      <TextWrapper>
        <Label type="large">{data.description}</Label>
        {renderText}
        {isPad && renderPadStatus()}
      </TextWrapper>
      {isTenant && isPending && !isPad && data.payment_status === 'incomplete' && (
        <ResolvePaymentButton size="medium" paymentData={data} />
      )}
    </Wrapper>
  )
}

export default PaymentCard
